import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, Chip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select,
  MenuItem, FormControl, InputLabel, Tooltip, Skeleton, Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import {
  ComposedChart, AreaChart, Area, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';
import { bloomberg } from '../theme/bloombergTheme';
import { API_BASE_URL } from '../services/api';
import { useIsMobile } from '../hooks/useIsMobile';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TrackerPortfolioMeta {
  id: number;
  name: string;
  currency: string;
  benchmark: string;
  benchmarks: string[];
  created_at: string;
  transaction_count: number;
}

interface TrackerTransaction {
  id: number;
  transaction_type: 'BUY' | 'SELL' | 'DEPOSIT' | 'WITHDRAWAL' | 'DIVIDEND';
  symbol?: string;
  quantity?: number;
  price?: number;
  total_amount: number;
  fees: number;
  transaction_date: string;
  notes?: string;
}

interface HoldingRow {
  symbol: string;
  quantity?: number;
  avg_cost?: number;
  current_price?: number;
  current_value?: number;
  unrealized_pnl?: number;
  unrealized_pct?: number;
  weight_pct?: number;
  is_cash?: boolean;
}

interface BenchmarkData {
  symbol: string;
  twr_pct: number;
  curve: { date: string; value: number }[];
}

interface TrackerSummary {
  current_value: number;
  cash_deposited: number;
  cash_available: number;
  total_invested: number;
  twr_pct: number;
  benchmark_twr_pct: number;
  active_return: number;
  unrealized_pnl: number;
  realized_pnl: number;
  total_pnl: number;
  holdings: HoldingRow[];
  curve: { date: string; twr_pct: number; portfolio_value: number; cost_basis: number }[];
  benchmark_curve: { date: string; value: number }[];
  benchmarks: BenchmarkData[];
  shadow_benchmark?: { symbol: string; curve: { date: string; value: number }[]; current_value: number } | null;
}

interface SymbolOption {
  symbol: string;
  name: string;
  currency: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TX_TYPES = ['BUY', 'SELL', 'DEPOSIT', 'WITHDRAWAL', 'DIVIDEND'] as const;

const TX_COLORS: Record<string, string> = {
  BUY: '#22c55e', SELL: '#ef4444', DEPOSIT: '#3b82f6',
  WITHDRAWAL: '#f59e0b', DIVIDEND: '#06b6d4',
};

// Distinct colors for benchmark lines (first = primary)
const BENCH_COLORS = ['#64748b', '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ec4899'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtCcy(v?: number | null, currency = 'USD') {
  if (v == null) return '—';
  const sym = currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$';
  return `${v < 0 ? '-' : ''}${sym}${Math.abs(v).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtPct(v?: number | null, sign = true) {
  if (v == null) return '—';
  return `${sign && v > 0 ? '+' : ''}${v.toFixed(2)}%`;
}

function pctColor(v?: number | null) {
  if (v == null) return 'inherit';
  return v > 0 ? '#22c55e' : v < 0 ? '#ef4444' : 'inherit';
}

const CELL_SX = { fontSize: 12, py: 0.6, px: 1, borderColor: 'rgba(255,255,255,0.06)' };
const HDR_SX  = { fontSize: 11, fontWeight: 700, color: bloomberg.text.secondary, py: 0.6, px: 1, borderColor: 'rgba(255,255,255,0.06)', whiteSpace: 'nowrap' as const };

// ─── KPI Card ─────────────────────────────────────────────────────────────────

const KpiCard: React.FC<{ label: string; value: string; sub?: string; color?: string }> = ({ label, value, sub, color }) => (
  <Paper elevation={2} sx={{ p: 1.5, minWidth: 130, flex: 1 }}>
    <Typography sx={{ fontSize: 10, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.3 }}>{label}</Typography>
    <Typography sx={{ fontSize: 18, fontWeight: 700, color: color || bloomberg.text.primary, lineHeight: 1.2 }}>{value}</Typography>
    {sub && <Typography sx={{ fontSize: 10, color: bloomberg.text.secondary, mt: 0.2 }}>{sub}</Typography>}
  </Paper>
);

// ─── Symbol Search Autocomplete ───────────────────────────────────────────────

const SymbolSearch: React.FC<{ onSelect: (symbol: string) => void; placeholder?: string; disabled?: boolean }> = ({
  onSelect, placeholder = 'Search symbol or name…', disabled,
}) => {
  const [options, setOptions] = useState<SymbolOption[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (inputValue.length < 1) { setOptions([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/tracker/search-symbols?q=${encodeURIComponent(inputValue)}`);
        const data: SymbolOption[] = await res.json();
        setOptions(data);
      } catch { setOptions([]); }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [inputValue]);

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      getOptionLabel={o => `${o.symbol} — ${o.name || ''}`}
      filterOptions={x => x}
      inputValue={inputValue}
      onInputChange={(_, v) => setInputValue(v)}
      onChange={(_, v) => {
        if (v) { onSelect(v.symbol); setInputValue(''); setOptions([]); }
      }}
      disabled={disabled}
      size="small"
      sx={{ minWidth: 240 }}
      renderInput={params => (
        <TextField {...params} placeholder={placeholder} size="small"
          sx={{ '& .MuiInputBase-input': { fontSize: 12 } }} />
      )}
      renderOption={(props, o) => (
        <li {...props} key={o.symbol} style={{ fontSize: 12 }}>
          <span style={{ fontWeight: 700, marginRight: 8, color: bloomberg.accent.orange }}>{o.symbol}</span>
          <span style={{ color: bloomberg.text.secondary }}>{o.name}</span>
          {o.currency && <span style={{ color: bloomberg.text.muted, marginLeft: 6, fontSize: 10 }}>{o.currency}</span>}
        </li>
      )}
      noOptionsText={inputValue.length < 1 ? 'Type to search…' : 'No results'}
    />
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const TrackerPortfolio: React.FC = () => {
  const isMobile = useIsMobile();
  const [portfolios, setPortfolios]         = useState<TrackerPortfolioMeta[]>([]);
  const [activePid, setActivePid]           = useState<number | null>(null);
  const [summary, setSummary]               = useState<TrackerSummary | null>(null);
  const [transactions, setTransactions]     = useState<TrackerTransaction[]>([]);
  const [loading, setLoading]               = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [chartTab, setChartTab]             = useState<'twr' | 'pnl'>('twr');

  // Portfolio create dialog
  const [createOpen, setCreateOpen]   = useState(false);
  const [newName, setNewName]         = useState('');
  const [newCurrency, setNewCurrency] = useState('USD');
  const [newBenchmark, setNewBenchmark] = useState('SPY');

  // Edit portfolio dialog
  const [editOpen, setEditOpen]       = useState(false);
  const [editName, setEditName]       = useState('');

  // Add transaction dialog
  const [txOpen, setTxOpen]           = useState(false);
  const [txType, setTxType]           = useState<string>('BUY');
  const [txSymbol, setTxSymbol]       = useState('');
  const [txQty, setTxQty]             = useState('');
  const [txPrice, setTxPrice]         = useState('');
  const [txAmount, setTxAmount]       = useState('');
  const [txFees, setTxFees]           = useState('');
  const [txDate, setTxDate]           = useState(new Date().toISOString().split('T')[0]);
  const [txNotes, setTxNotes]         = useState('');
  const [txSubmitting, setTxSubmitting] = useState(false);

  const needsSymbol = ['BUY', 'SELL', 'DIVIDEND'].includes(txType);
  const cashAvailable = summary?.cash_available ?? 0;
  const txAmountNum = parseFloat(txAmount) || 0;
  const insufficientCash = txType === 'BUY' && txAmountNum > cashAvailable + 0.005;

  // Auto-compute total from qty × price
  useEffect(() => {
    if (needsSymbol && txQty && txPrice) {
      const t = parseFloat(txQty) * parseFloat(txPrice);
      if (!isNaN(t)) setTxAmount(t.toFixed(2));
    }
  }, [txQty, txPrice, needsSymbol]);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchPortfolios = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/tracker/portfolios`);
      const data: TrackerPortfolioMeta[] = await res.json();
      setPortfolios(data);
      if (data.length > 0 && !activePid) setActivePid(data[0].id);
    } catch (e) { console.error(e); }
  }, [activePid]);

  const fetchSummary = useCallback(async (pid: number) => {
    setSummaryLoading(true);
    setSummary(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/tracker/portfolios/${pid}`);
      const data = await res.json();
      setSummary(data as TrackerSummary);
    } catch (e) { console.error(e); }
    finally { setSummaryLoading(false); }
  }, []);

  const fetchTransactions = useCallback(async (pid: number) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/tracker/portfolios/${pid}/transactions`);
      const data: TrackerTransaction[] = await res.json();
      setTransactions(data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchPortfolios(); }, []);

  useEffect(() => {
    if (activePid) {
      fetchSummary(activePid);
      fetchTransactions(activePid);
    }
  }, [activePid]);

  // ── Benchmark management ───────────────────────────────────────────────────

  const addBenchmark = async (symbol: string) => {
    if (!activePid) return;
    await fetch(`${API_BASE_URL}/api/tracker/portfolios/${activePid}/benchmarks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ symbol }),
    });
    await fetchPortfolios();
    await fetchSummary(activePid);
  };

  const removeBenchmark = async (symbol: string) => {
    if (!activePid) return;
    await fetch(`${API_BASE_URL}/api/tracker/portfolios/${activePid}/benchmarks/${symbol}`, { method: 'DELETE' });
    await fetchPortfolios();
    await fetchSummary(activePid);
  };

  // ── Portfolio actions ──────────────────────────────────────────────────────

  const handleCreatePortfolio = async () => {
    if (!newName.trim()) return;
    setLoading(true);
    try {
      await fetch(`${API_BASE_URL}/api/tracker/portfolios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim(), currency: newCurrency, benchmark: newBenchmark }),
      });
      setCreateOpen(false); setNewName('');
      await fetchPortfolios();
    } finally { setLoading(false); }
  };

  const handleUpdatePortfolio = async () => {
    if (!activePid) return;
    await fetch(`${API_BASE_URL}/api/tracker/portfolios/${activePid}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: editName }),
    });
    setEditOpen(false);
    await fetchPortfolios();
  };

  const handleDeletePortfolio = async () => {
    if (!activePid || !window.confirm('Delete this portfolio and all its transactions?')) return;
    await fetch(`${API_BASE_URL}/api/tracker/portfolios/${activePid}`, { method: 'DELETE' });
    setActivePid(null);
    setSummary(null);
    setTransactions([]);
    await fetchPortfolios();
  };

  // ── Transactions ───────────────────────────────────────────────────────────

  const handleAddTransaction = async () => {
    if (!activePid) return;
    setTxSubmitting(true);
    try {
      await fetch(`${API_BASE_URL}/api/tracker/portfolios/${activePid}/transactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transaction_type: txType,
          symbol: needsSymbol ? txSymbol.trim().toUpperCase() : undefined,
          quantity: needsSymbol && txQty ? parseFloat(txQty) : undefined,
          price:    needsSymbol && txPrice ? parseFloat(txPrice) : undefined,
          total_amount: parseFloat(txAmount) || 0,
          fees: parseFloat(txFees) || 0,
          transaction_date: txDate,
          notes: txNotes,
        }),
      });
      setTxOpen(false);
      resetTxForm();
      await fetchTransactions(activePid);
      await fetchSummary(activePid);
    } finally { setTxSubmitting(false); }
  };

  const handleDeleteTransaction = async (tid: number) => {
    if (!activePid || !window.confirm('Delete this transaction?')) return;
    await fetch(`${API_BASE_URL}/api/tracker/portfolios/${activePid}/transactions/${tid}`, { method: 'DELETE' });
    await fetchTransactions(activePid);
    await fetchSummary(activePid);
  };

  const resetTxForm = () => {
    setTxType('BUY'); setTxSymbol(''); setTxQty(''); setTxPrice('');
    setTxAmount(''); setTxFees(''); setTxDate(new Date().toISOString().split('T')[0]); setTxNotes('');
  };

  // ── Chart data ─────────────────────────────────────────────────────────────

  const activePortfolio = portfolios.find(p => p.id === activePid);
  const currency        = activePortfolio?.currency || 'USD';
  const activeBenchmarks = summary?.benchmarks || [];

  const twrChartData = React.useMemo(() => {
    if (!summary) return [];
    const maps: Record<string, Record<string, number>> = {};
    activeBenchmarks.forEach(b => {
      maps[b.symbol] = {};
      b.curve.forEach(pt => { maps[b.symbol][pt.date] = pt.value; });
    });
    return (summary.curve || []).map(c => {
      const row: Record<string, number | string | null> = {
        date: c.date,
        portfolio: parseFloat(((1 + c.twr_pct / 100) * 100).toFixed(2)),
      };
      activeBenchmarks.forEach(b => {
        row[`bench_${b.symbol}`] = maps[b.symbol][c.date] ?? null;
      });
      return row;
    });
  }, [summary, activeBenchmarks]);

  const pnlChartData = React.useMemo(() => {
    if (!summary) return [];
    const shadowMap: Record<string, number> = {};
    (summary.shadow_benchmark?.curve || []).forEach(p => { shadowMap[p.date] = p.value; });
    return (summary.curve || []).map(c => ({
      date: c.date,
      value: c.portfolio_value,
      cost:  c.cost_basis,
      pnl:   parseFloat((c.portfolio_value - c.cost_basis).toFixed(2)),
      shadow: shadowMap[c.date] ?? null,
    }));
  }, [summary]);

  const cashFlowDates = React.useMemo(() =>
    transactions
      .filter(t => ['DEPOSIT', 'WITHDRAWAL'].includes(t.transaction_type))
      .map(t => t.transaction_date),
    [transactions]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 2, maxWidth: 1400, mx: 'auto' }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <AccountBalanceIcon sx={{ color: bloomberg.accent.orange, fontSize: 22 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: bloomberg.accent.orange, mr: 1 }}>
          Investment Tracker
        </Typography>

        {/* Portfolio selector */}
        {portfolios.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={activePid ?? ''}
              onChange={e => setActivePid(Number(e.target.value))}
              sx={{ fontSize: 13, height: 32 }}
            >
              {portfolios.map(p => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Button size="small" startIcon={<AddIcon />} variant="outlined"
          onClick={() => setCreateOpen(true)}
          sx={{ fontSize: 11, height: 28, borderColor: bloomberg.border, color: bloomberg.text.secondary }}>
          New Portfolio
        </Button>

        {activePid && (
          <>
            <Tooltip title="Rename portfolio">
              <IconButton size="small" onClick={() => {
                const p = portfolios.find(x => x.id === activePid);
                if (p) { setEditName(p.name); setEditOpen(true); }
              }}>
                <EditIcon sx={{ fontSize: 16, color: bloomberg.text.muted }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete portfolio">
              <IconButton size="small" onClick={handleDeletePortfolio}>
                <DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {/* ── Benchmark row ── */}
      {activePid && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 11, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.05em', mr: 0.5 }}>
            Benchmarks:
          </Typography>
          {(activePortfolio?.benchmarks || []).map((sym, i) => (
            <Chip
              key={sym}
              label={sym}
              size="small"
              onDelete={() => removeBenchmark(sym)}
              deleteIcon={<CloseIcon />}
              sx={{
                height: 22, fontSize: 11, fontWeight: 700,
                bgcolor: `${BENCH_COLORS[i % BENCH_COLORS.length]}22`,
                color: BENCH_COLORS[i % BENCH_COLORS.length],
                border: `1px solid ${BENCH_COLORS[i % BENCH_COLORS.length]}66`,
                '& .MuiChip-deleteIcon': { fontSize: 12, color: BENCH_COLORS[i % BENCH_COLORS.length] },
              }}
            />
          ))}
          <SymbolSearch
            placeholder="Add benchmark…"
            onSelect={addBenchmark}
            disabled={!activePid}
          />
        </Box>
      )}

      {/* ── Empty state ── */}
      {portfolios.length === 0 && (
        <Paper elevation={2} sx={{ p: 5, textAlign: 'center' }}>
          <AccountBalanceIcon sx={{ fontSize: 48, color: bloomberg.text.muted, mb: 2 }} />
          <Typography sx={{ color: bloomberg.text.secondary, mb: 2 }}>
            Create your first portfolio to start tracking time-weighted returns.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
            sx={{ bgcolor: bloomberg.accent.orange, color: '#000', '&:hover': { bgcolor: '#e08800' } }}>
            Create Portfolio
          </Button>
        </Paper>
      )}

      {activePid && (
        <>
          {/* ── KPI Bar ── */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            {summaryLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} variant="rectangular" width={140} height={72} sx={{ borderRadius: 1 }} />
              ))
            ) : summary && isMobile ? (
              // Simplified mobile view — a curated 4-card set instead of the
              // full ~13-card flex-wrap row, which just becomes a wall of
              // small tiles at phone width.
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.25, width: '100%' }}>
                <KpiCard label="Portfolio Value" value={fmtCcy(summary.current_value, currency)} />
                <KpiCard label="Total P&L" value={fmtCcy(summary.total_pnl, currency)} color={pctColor(summary.total_pnl)} />
                <KpiCard label="TWR" value={fmtPct(summary.twr_pct)} color={pctColor(summary.twr_pct)} />
                {activeBenchmarks.length > 0 ? (
                  <KpiCard
                    label="Alpha"
                    value={fmtPct(summary.active_return)}
                    color={pctColor(summary.active_return)}
                    sub={summary.active_return > 0 ? 'Beating index' : summary.active_return < 0 ? 'Trailing index' : 'In line'}
                  />
                ) : (
                  <KpiCard
                    label="Profit %"
                    value={fmtPct(summary.total_invested ? (summary.total_pnl / summary.total_invested) * 100 : null)}
                    color={pctColor(summary.total_pnl)}
                    sub="Return on cost"
                  />
                )}
              </Box>
            ) : summary ? (
              <>
                <KpiCard label="Cash Deposited" value={fmtCcy(summary.cash_deposited, currency)} />
                <KpiCard
                  label="Cash Available"
                  value={fmtCcy(summary.cash_available, currency)}
                  color={summary.cash_available < 0 ? '#ef4444' : undefined}
                  sub="Not yet invested"
                />
                <KpiCard label="Cost Basis" value={fmtCcy(summary.total_invested, currency)} sub="Open positions" />
                <KpiCard label="Portfolio Value" value={fmtCcy(summary.current_value, currency)} />
                <KpiCard
                  label="Total P&L"
                  value={fmtCcy(summary.total_pnl, currency)}
                  color={pctColor(summary.total_pnl)}
                />
                <KpiCard
                  label="Profit %"
                  value={fmtPct(summary.total_invested ? (summary.total_pnl / summary.total_invested) * 100 : null)}
                  color={pctColor(summary.total_pnl)}
                  sub="Return on cost"
                />
                <KpiCard label="Unrealised P&L" value={fmtCcy(summary.unrealized_pnl, currency)} color={pctColor(summary.unrealized_pnl)} />
                <KpiCard label="Realised P&L" value={fmtCcy(summary.realized_pnl, currency)} color={pctColor(summary.realized_pnl)} />
                <KpiCard label="TWR" value={fmtPct(summary.twr_pct)} color={pctColor(summary.twr_pct)} />
                {activeBenchmarks.map((b, i) => (
                  <KpiCard
                    key={b.symbol}
                    label={`${b.symbol} Return`}
                    value={fmtPct(b.twr_pct)}
                    color={BENCH_COLORS[i % BENCH_COLORS.length]}
                  />
                ))}
                {activeBenchmarks.length > 0 && (
                  <KpiCard
                    label="Alpha"
                    value={fmtPct(summary.active_return)}
                    color={pctColor(summary.active_return)}
                    sub={summary.active_return > 0 ? 'Beating index' : summary.active_return < 0 ? 'Trailing index' : 'In line'}
                  />
                )}
                {summary.shadow_benchmark && summary.shadow_benchmark.current_value > 0 && (
                  <>
                    <KpiCard
                      label={`Shadow (${summary.shadow_benchmark.symbol})`}
                      value={fmtCcy(summary.shadow_benchmark.current_value, currency)}
                      sub="Index equiv. of your buys"
                    />
                    <KpiCard
                      label="vs Shadow %"
                      value={fmtPct((summary.current_value - summary.shadow_benchmark.current_value) / summary.shadow_benchmark.current_value * 100)}
                      color={pctColor(summary.current_value - summary.shadow_benchmark.current_value)}
                      sub={`${fmtCcy(summary.current_value - summary.shadow_benchmark.current_value, currency)} ${summary.current_value >= summary.shadow_benchmark.current_value ? 'ahead' : 'behind'}`}
                    />
                  </>
                )}
              </>
            ) : null}
          </Box>

          {/* ── Chart Panel ── */}
          <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
            {/* Tab bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {(['twr', 'pnl'] as const).map(tab => (
                  <Button key={tab} size="small" onClick={() => setChartTab(tab)}
                    sx={{
                      fontSize: 11, height: 26, px: 1.5, minWidth: 0,
                      fontWeight: chartTab === tab ? 700 : 400,
                      color: chartTab === tab ? bloomberg.accent.orange : bloomberg.text.muted,
                      borderBottom: chartTab === tab ? `2px solid ${bloomberg.accent.orange}` : '2px solid transparent',
                      borderRadius: 0,
                    }}>
                    {tab === 'twr' ? 'TWR vs Benchmark' : 'P&L'}
                  </Button>
                ))}
              </Box>
            </Box>

            {summaryLoading ? (
              <Skeleton variant="rectangular" height={240} />
            ) : chartTab === 'twr' ? (
              twrChartData.length > 1 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <ComposedChart data={twrChartData} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: bloomberg.text.muted }} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: bloomberg.text.muted }} tickLine={false} axisLine={false}
                      tickFormatter={v => `${(v as number).toFixed(0)}`} />
                    <RechartsTooltip
                      contentStyle={{ backgroundColor: bloomberg.bg.secondary, border: `1px solid ${bloomberg.border}`, fontSize: 11 }}
                      formatter={(val: number, name: string) => {
                        if (name === 'portfolio') return [val?.toFixed(2), 'Portfolio (TWR, base 100)'];
                        return [val?.toFixed(2), name.replace('bench_', '')];
                      }}
                    />
                    <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }}
                      formatter={(v: string) => v === 'portfolio' ? 'Portfolio (TWR)' : v.replace('bench_', '')} />
                    {cashFlowDates.map(d => (
                      <ReferenceLine key={d} x={d} stroke={bloomberg.text.muted} strokeDasharray="4 2" strokeWidth={1} />
                    ))}
                    <Line type="monotone" dataKey="portfolio" stroke={bloomberg.accent.orange} strokeWidth={2} dot={false} connectNulls name="portfolio" />
                    {activeBenchmarks.map((b, i) => (
                      <Line key={b.symbol} type="monotone" dataKey={`bench_${b.symbol}`}
                        stroke={BENCH_COLORS[i % BENCH_COLORS.length]} strokeWidth={1.5}
                        strokeDasharray="6 3" dot={false} connectNulls name={`bench_${b.symbol}`} />
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 12, color: bloomberg.text.muted }}>Add transactions to see performance.</Typography>
                </Box>
              )
            ) : (
              pnlChartData.length > 1 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={pnlChartData} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="gradValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={bloomberg.accent.orange} stopOpacity={0.25} />
                        <stop offset="95%" stopColor={bloomberg.accent.orange} stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="gradCost" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#64748b" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#64748b" stopOpacity={0.01} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: bloomberg.text.muted }} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: bloomberg.text.muted }} tickLine={false} axisLine={false}
                      tickFormatter={v => {
                        const n = v as number;
                        return n >= 1000000 ? `${(n/1000000).toFixed(1)}M` : n >= 1000 ? `${(n/1000).toFixed(0)}k` : `${n.toFixed(0)}`;
                      }} />
                    <RechartsTooltip
                      contentStyle={{ backgroundColor: bloomberg.bg.secondary, border: `1px solid ${bloomberg.border}`, fontSize: 11 }}
                      formatter={(val: number, name: string) => {
                        if (name === 'value') return [fmtCcy(val, currency), 'Portfolio Value'];
                        if (name === 'cost') return [fmtCcy(val, currency), 'Net Invested'];
                        if (name === 'shadow') return [fmtCcy(val, currency), `Shadow (${summary?.shadow_benchmark?.symbol})`];
                        return [<span style={{ color: pctColor(val) }}>{fmtCcy(val, currency)}</span>, 'P&L'];
                      }}
                    />
                    <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }}
                      formatter={(v: string) =>
                        v === 'value' ? 'Portfolio Value' :
                        v === 'cost' ? 'Net Invested' :
                        v === 'shadow' ? `Shadow (${summary?.shadow_benchmark?.symbol ?? 'Benchmark'})` :
                        'P&L'
                      } />
                    {cashFlowDates.map(d => (
                      <ReferenceLine key={d} x={d} stroke={bloomberg.text.muted} strokeDasharray="4 2" strokeWidth={1} />
                    ))}
                    <Area type="monotone" dataKey="value" stroke={bloomberg.accent.orange} strokeWidth={2}
                      fill="url(#gradValue)" dot={false} connectNulls name="value" />
                    <Area type="monotone" dataKey="cost" stroke="#64748b" strokeWidth={1.5}
                      strokeDasharray="5 3" fill="url(#gradCost)" dot={false} connectNulls name="cost" />
                    {summary?.shadow_benchmark && (
                      <Area type="monotone" dataKey="shadow" stroke="#a855f7" strokeWidth={1.5}
                        strokeDasharray="4 2" fill="none" dot={false} connectNulls name="shadow" />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 12, color: bloomberg.text.muted }}>Add transactions to see P&L chart.</Typography>
                </Box>
              )
            )}
          </Paper>

          {/* ── Holdings + Transactions ── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>

            {/* Holdings */}
            <Paper elevation={2} sx={{ p: 2, overflow: 'auto' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: bloomberg.text.secondary, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Holdings
              </Typography>
              {summaryLoading ? (
                <Skeleton variant="rectangular" height={120} />
              ) : (summary?.holdings || []).length === 0 ? (
                <Typography sx={{ fontSize: 12, color: bloomberg.text.muted, py: 2, textAlign: 'center' }}>No positions yet.</Typography>
              ) : isMobile ? (
                // Mobile: a 7-column table has no room to breathe at phone
                // width — a shrunk, horizontally-scrolling table is
                // technically viewable but not "designed", so this is a
                // stacked card per holding instead, reading from the exact
                // same holdings array as the desktop table below.
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {(summary?.holdings || []).map(h => (
                    <Box key={h.symbol} sx={{
                      p: 1.25, borderRadius: 1,
                      border: '1px solid rgba(255,255,255,0.08)',
                      background: h.is_cash ? 'rgba(59,130,246,0.06)' : 'transparent',
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: h.is_cash ? '#3b82f6' : bloomberg.accent.orange }}>
                          {h.symbol}
                          {h.is_cash && <span style={{ fontSize: 9, fontWeight: 400, color: bloomberg.text.muted, marginLeft: 4 }}>Available</span>}
                        </Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{fmtCcy(h.current_value, currency)}</Typography>
                      </Box>
                      {!h.is_cash && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                          <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>
                            {h.quantity != null ? h.quantity.toFixed(4) : '—'} @ {h.avg_cost ? fmtCcy(h.avg_cost, currency) : '—'}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: pctColor(h.unrealized_pct) }}>
                            {h.unrealized_pnl != null ? fmtCcy(h.unrealized_pnl, currency) : '—'}
                            {h.unrealized_pct != null ? ` (${fmtPct(h.unrealized_pct)})` : ''}
                            {h.weight_pct != null ? ` · ${h.weight_pct.toFixed(1)}%` : ''}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Symbol', 'Qty', 'Avg Cost', 'Price', 'Value', 'P&L', 'Weight'].map(h => (
                        <th key={h} style={{ ...HDR_SX, textAlign: h === 'Symbol' ? 'left' : 'right', paddingBottom: 6 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(summary?.holdings || []).map(h => (
                      <tr key={h.symbol} style={{
                        borderTop: h.is_cash ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(255,255,255,0.05)',
                        background: h.is_cash ? 'rgba(59,130,246,0.06)' : undefined,
                      }}>
                        <td style={{ ...CELL_SX, fontWeight: 600, color: h.is_cash ? '#3b82f6' : bloomberg.accent.orange }}>
                          {h.symbol}
                          {h.is_cash && <span style={{ fontSize: 9, fontWeight: 400, color: bloomberg.text.muted, marginLeft: 4 }}>Available</span>}
                        </td>
                        <td style={{ ...CELL_SX, textAlign: 'right', color: bloomberg.text.muted }}>
                          {h.is_cash ? '—' : h.quantity != null ? h.quantity.toFixed(4) : '—'}
                        </td>
                        <td style={{ ...CELL_SX, textAlign: 'right', color: bloomberg.text.muted }}>
                          {h.is_cash ? '—' : h.avg_cost ? fmtCcy(h.avg_cost, currency) : '—'}
                        </td>
                        <td style={{ ...CELL_SX, textAlign: 'right' }}>
                          {h.is_cash ? '—' : h.current_price ? fmtCcy(h.current_price, currency) : '—'}
                        </td>
                        <td style={{ ...CELL_SX, textAlign: 'right', fontWeight: 600, color: h.is_cash ? '#3b82f6' : undefined }}>
                          {fmtCcy(h.current_value, currency)}
                        </td>
                        <td style={{ ...CELL_SX, textAlign: 'right', color: h.is_cash ? bloomberg.text.muted : pctColor(h.unrealized_pct) }}>
                          {h.is_cash ? '—' : (
                            <>
                              {h.unrealized_pnl != null ? fmtCcy(h.unrealized_pnl, currency) : '—'}
                              {h.unrealized_pct != null ? ` (${fmtPct(h.unrealized_pct)})` : ''}
                            </>
                          )}
                        </td>
                        <td style={{ ...CELL_SX, textAlign: 'right', color: bloomberg.text.muted }}>
                          {h.weight_pct != null ? `${h.weight_pct.toFixed(1)}%` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Paper>

            {/* Transactions */}
            <Paper elevation={2} sx={{ p: 2, overflow: 'auto' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: bloomberg.text.secondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Transactions
                </Typography>
                <Button size="small" startIcon={<AddIcon />} variant="contained"
                  onClick={() => { resetTxForm(); setTxOpen(true); }}
                  sx={{ fontSize: 10, height: 26, bgcolor: bloomberg.accent.orange, color: '#000', '&:hover': { bgcolor: '#e08800' } }}>
                  Add
                </Button>
              </Box>

              {transactions.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: bloomberg.text.muted, py: 2, textAlign: 'center' }}>No transactions yet.</Typography>
              ) : isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {transactions.map(t => (
                    <Box key={t.id} sx={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      p: 1, borderRadius: 1, border: '1px solid rgba(255,255,255,0.08)',
                    }}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <span style={{
                            display: 'inline-block', padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                            background: `${TX_COLORS[t.transaction_type]}22`,
                            color: TX_COLORS[t.transaction_type],
                          }}>
                            {t.transaction_type}
                          </span>
                          {t.symbol && <Typography component="span" sx={{ fontSize: 12, fontWeight: 600 }}>{t.symbol}</Typography>}
                        </Box>
                        <Typography sx={{ fontSize: 10, color: bloomberg.text.muted, mt: 0.25 }}>{t.transaction_date}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: t.transaction_type === 'WITHDRAWAL' ? '#ef4444' : '#22c55e' }}>
                          {t.transaction_type === 'WITHDRAWAL' ? '-' : ''}{fmtCcy(t.total_amount, currency)}
                        </Typography>
                        <IconButton size="small" onClick={() => handleDeleteTransaction(t.id)}>
                          <DeleteIcon sx={{ fontSize: 14, color: bloomberg.text.muted }} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Date', 'Type', 'Symbol', 'Amount', ''].map((h, i) => (
                        <th key={i} style={{ ...HDR_SX, textAlign: i === 3 ? 'right' : 'left', paddingBottom: 6 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(t => (
                      <tr key={t.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                        <td style={{ ...CELL_SX, color: bloomberg.text.muted, whiteSpace: 'nowrap' }}>{t.transaction_date}</td>
                        <td style={{ ...CELL_SX }}>
                          <span style={{
                            display: 'inline-block', padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                            background: `${TX_COLORS[t.transaction_type]}22`,
                            color: TX_COLORS[t.transaction_type],
                          }}>
                            {t.transaction_type}
                          </span>
                        </td>
                        <td style={{ ...CELL_SX, fontWeight: 600 }}>{t.symbol || '—'}</td>
                        <td style={{ ...CELL_SX, textAlign: 'right' }}>
                          {t.transaction_type === 'WITHDRAWAL'
                            ? <span style={{ color: '#ef4444' }}>-{fmtCcy(t.total_amount, currency)}</span>
                            : <span style={{ color: '#22c55e' }}>{fmtCcy(t.total_amount, currency)}</span>
                          }
                          {t.fees > 0 && <span style={{ color: bloomberg.text.muted, fontSize: 10 }}> (-{fmtCcy(t.fees, currency)})</span>}
                        </td>
                        <td style={{ ...CELL_SX, textAlign: 'right' }}>
                          <IconButton size="small" onClick={() => handleDeleteTransaction(t.id)}>
                            <DeleteIcon sx={{ fontSize: 14, color: bloomberg.text.muted }} />
                          </IconButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Paper>
          </Box>
        </>
      )}

      {/* ── Create Portfolio Dialog ── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 14, fontWeight: 700 }}>New Portfolio</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          <TextField label="Name" value={newName} onChange={e => setNewName(e.target.value)} size="small" fullWidth autoFocus />
          <FormControl size="small" fullWidth>
            <InputLabel>Currency</InputLabel>
            <Select value={newCurrency} label="Currency" onChange={e => setNewCurrency(e.target.value)}>
              {['USD', 'GBP', 'EUR', 'CHF', 'SEK', 'NOK'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Primary benchmark (e.g. SPY, QQQ, IWDA.L)" value={newBenchmark}
            onChange={e => setNewBenchmark(e.target.value.toUpperCase())} size="small" fullWidth
            helperText="You can add more benchmarks after creating the portfolio" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} size="small">Cancel</Button>
          <Button onClick={handleCreatePortfolio} variant="contained" size="small" disabled={!newName.trim() || loading}
            sx={{ bgcolor: bloomberg.accent.orange, color: '#000' }}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Portfolio Dialog ── */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 14, fontWeight: 700 }}>Rename Portfolio</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          <TextField label="Name" value={editName} onChange={e => setEditName(e.target.value)} size="small" fullWidth autoFocus />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} size="small">Cancel</Button>
          <Button onClick={handleUpdatePortfolio} variant="contained" size="small"
            sx={{ bgcolor: bloomberg.accent.orange, color: '#000' }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add Transaction Dialog ── */}
      <Dialog open={txOpen} onClose={() => setTxOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 14, fontWeight: 700 }}>Add Transaction</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: '12px !important' }}>
          {/* Type chips */}
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {TX_TYPES.map(type => (
              <Chip key={type} label={type} size="small" clickable onClick={() => setTxType(type)}
                sx={{
                  fontSize: 11, fontWeight: 700, height: 26,
                  bgcolor: txType === type ? `${TX_COLORS[type]}22` : 'transparent',
                  color: txType === type ? TX_COLORS[type] : bloomberg.text.muted,
                  border: `1px solid ${txType === type ? TX_COLORS[type] : bloomberg.border}`,
                }}
              />
            ))}
          </Box>

          <TextField label="Date" type="date" value={txDate}
            onChange={e => setTxDate(e.target.value)}
            size="small" fullWidth InputLabelProps={{ shrink: true }} />

          {needsSymbol && (
            <TextField label="Symbol (e.g. AAPL)" value={txSymbol}
              onChange={e => setTxSymbol(e.target.value.toUpperCase())} size="small" fullWidth />
          )}

          {needsSymbol && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Quantity" value={txQty} onChange={e => setTxQty(e.target.value)}
                size="small" type="number" inputProps={{ min: 0 }} sx={{ flex: 1 }} />
              <TextField label="Price per share" value={txPrice} onChange={e => setTxPrice(e.target.value)}
                size="small" type="number" inputProps={{ min: 0 }} sx={{ flex: 1 }} />
            </Box>
          )}

          <TextField
            label={needsSymbol ? 'Total Amount (auto-calculated)' : 'Amount'}
            value={txAmount} onChange={e => setTxAmount(e.target.value)}
            size="small" fullWidth type="number" inputProps={{ min: 0 }}
            error={insufficientCash}
            helperText={
              insufficientCash
                ? `Insufficient cash — ${fmtCcy(cashAvailable, currency)} available`
                : needsSymbol ? 'Qty × Price — override if needed' : undefined
            }
          />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField label="Fees (optional)" value={txFees} onChange={e => setTxFees(e.target.value)}
              size="small" type="number" inputProps={{ min: 0 }} sx={{ flex: 1 }} />
            <TextField label="Notes (optional)" value={txNotes} onChange={e => setTxNotes(e.target.value)}
              size="small" sx={{ flex: 2 }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTxOpen(false)} size="small">Cancel</Button>
          <Button onClick={handleAddTransaction} variant="contained" size="small"
            disabled={!txAmount || txSubmitting || insufficientCash}
            sx={{ bgcolor: bloomberg.accent.orange, color: '#000' }}>
            {txSubmitting ? <CircularProgress size={14} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrackerPortfolio;
